import { useState, useEffect } from 'react';

// Same 768px breakpoint/pattern used elsewhere in julkisivuV2 (PeitepaksuusView,
// KuntoarvioWorkspaceV2, etc.) — below it, the tarkastuskohde tab row and
// rakenneosa tray both switch to a flat, single-accent-color grouped list
// (see render below) instead of colored tabs/pills, matching Apple-style
// "grouped list" conventions (iOS Settings) rather than the desktop tab/
// tray look. A vertical list also has no overflow problem regardless of
// tarkastuskohde count, unlike a horizontal tab row.
function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return mobile;
}

// Pure view — every value and handler comes from useRakenneosaNavV2. No API
// calls, no state beyond the form text inputs (view-local UX, not business
// logic). Tarkastuskohteet (created in Kuntoarviovalikko) render as tabs;
// rakenneosat within the active tab render as pills — both stay visible and
// switchable at any time rather than hiding once one is picked, so moving
// between e.g. "Asuntojen parvekkeet" and "Julkisivut" is a single click.
// "Lisää rakenneosa" opens a modal matching v1 PoranayteBlock's own add
// modal (overlay, pills of common rakenneosa-tyypit + a custom name input).
// pending: a change is already in flight (optimistic, not yet confirmed) —
// blur + dim every tab/pill and block further clicks until it resolves, so
// a second click can't start an overlapping save that might resolve out of
// order and flicker the selection back to the previous one.
// "Connected tray" treatment: the active tk tab is top-rounded only and its
// bottom border matches the tray's own background (white), so with the tray
// positioned directly beneath it (see trayStyle) the two visually fuse into
// one shape — the tab reads as the tray's own header, not an unrelated
// button sitting above an unrelated row.
const tabStyle = (active, pending) => ({
  border: '1px solid ' + (active ? '#198754' : '#ced4da'),
  borderBottom: active ? '1px solid #fff' : '1px solid #ced4da',
  background: active ? '#198754' : '#fff',
  color: active ? '#fff' : '#495057',
  borderRadius: '10px 10px 0 0', padding: '8px 16px', fontSize: '0.85rem', fontWeight: 600,
  position: active ? 'relative' : 'static', zIndex: active ? 2 : 1,
  cursor: pending ? 'wait' : 'pointer',
  filter: pending ? 'blur(1.5px)' : 'none', opacity: pending ? 0.6 : 1,
  transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, filter 0.15s ease, opacity 0.15s ease',
});
const trayStyle = {
  marginTop: -1, border: '1px solid #198754', borderTop: 'none', borderRadius: '0 10px 10px 10px',
  background: '#fff', padding: '14px 16px 16px', boxShadow: '0 2px 10px rgba(25,135,84,0.06)',
};
const trayLabelStyle = {
  fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
  color: '#198754', marginBottom: 8,
};
// Smaller and squarer than the tk tabs above (not a second row of
// equal-weight pills), and the inactive state is a light tint of the same
// blue family instead of plain white/gray — reads as "secondary, same
// family" rather than a second unrelated control.
const pillStyle = (active, pending) => ({
  padding: '6px 14px', borderRadius: 8, border: '1px solid ' + (active ? '#0d6efd' : '#d6e4fc'),
  background: active ? '#0d6efd' : '#f3f7ff', color: active ? '#fff' : '#3860b0',
  fontSize: '0.78rem', fontWeight: 600,
  cursor: pending ? 'wait' : 'pointer',
  filter: pending ? 'blur(1.5px)' : 'none', opacity: pending ? 0.6 : 1,
  transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, filter 0.15s ease, opacity 0.15s ease',
});
const crumbBtn = { border: 'none', background: 'none', color: '#0d6efd', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem', padding: 0 };
// Trailing tab (e.g. "PTS" in the standalone kuntoarvio workspace) — same
// green family and top-rounded "connects to a tray" shape as tabStyle so it
// doesn't clash, but ghost/outline at rest (filled only once active, same
// as tabStyle's active state) and set apart from the tarkastuskohde tabs by
// a thin vertical divider rather than a different hue — reads as "a
// different kind of tab" without the row looking like a mixed color palette.
const trailingTabStyle = (active, pending) => ({
  border: '1px solid #198754',
  borderBottom: active ? '1px solid #fff' : '1px solid #198754',
  background: active ? '#198754' : '#fff',
  color: active ? '#fff' : '#198754',
  borderRadius: '10px 10px 0 0', padding: '8px 16px', fontSize: '0.82rem', fontWeight: 700,
  position: active ? 'relative' : 'static', zIndex: active ? 2 : 1,
  cursor: pending ? 'wait' : 'pointer',
  filter: pending ? 'blur(1.5px)' : 'none', opacity: pending ? 0.6 : 1,
  transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, filter 0.15s ease, opacity 0.15s ease',
});
const trailingDividerStyle = { width: 1, flexShrink: 0, alignSelf: 'stretch', background: '#dee2e6', margin: '4px 6px 4px 2px' };

// Inline styles can't use :hover, so tabs/pills/buttons get this handler pair
// (same pattern the v1 blocks already use for their "×" delete buttons) —
// a small lift + shadow on hover, undone on leave.
const hoverLift = {
  onMouseEnter: e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.12)'; },
  onMouseLeave: e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; },
};

export default function RakenneosaNavView({ id_kiinteisto, svc, extraActions = null, canCreateRakenneosa = false, showRakenneosaTabs = true, trailingTab = null }) {
  const { nav, navPending, ptsProjects, currentPtsProject, rakTyypit, tarkastuskohteet, rakenneosat, error,
    linkedProjectId,
    pickPtsProject, createPtsProject,
    openTk, addRak, openRak,
    goToPts } = svc;

  const [newPtsName, setNewPtsName] = useState('');
  const [showAddRak, setShowAddRak] = useState(false);
  const [newRakNimi, setNewRakNimi] = useState('');
  const [newRakTyyppi, setNewRakTyyppi] = useState('');
  const isMobile = useIsMobile();

  if (!id_kiinteisto) {
    return <div style={{ color: '#842029', background: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: 4, padding: '8px 12px', fontSize: '0.82rem' }}>
      Kiinteistön ID ei saatavilla tälle lohkolle.
    </div>;
  }

  const crumbs = linkedProjectId ? [] : [{ label: 'PTS-projekti', onClick: goToPts }];
  if (currentPtsProject) {
    const label = (currentPtsProject.name || currentPtsProject.nimi || `PTS ${currentPtsProject.id}`) + (linkedProjectId ? ' (Kuntoarviovalikosta)' : '');
    crumbs.push({ label, onClick: () => {} });
  }

  const openAddRak = () => { setNewRakNimi(''); setNewRakTyyppi(''); setShowAddRak(true); };
  const closeAddRak = () => setShowAddRak(false);
  const trimmedNimi = newRakNimi.trim();
  const dup = trimmedNimi && rakenneosat.find(r => r.nimi.trim().toLowerCase() === trimmedNimi.toLowerCase());
  const submitAddRak = () => {
    if (!trimmedNimi) return;
    addRak(newRakNimi, newRakTyyppi);
    setShowAddRak(false);
  };

  return (
    <div style={{ marginBottom: 14 }}>
      {crumbs.length > 0 && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.78rem', color: '#6c757d', marginBottom: 10, flexWrap: 'wrap' }}>
          {crumbs.map((c, i) => (
            <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {i > 0 && <span style={{ color: '#ced4da' }}>›</span>}
              <button type="button" style={crumbBtn} onClick={c.onClick}>{c.label}</button>
            </span>
          ))}
        </div>
      )}
      {error && <div style={{ color: '#842029', background: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: 4, padding: '6px 10px', fontSize: '0.78rem', marginBottom: 8 }}>{error}</div>}

      {!nav.ptsProjectId && (
        <div>
          {ptsProjects.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 16px', color: '#6c757d', borderRadius: 6, background: '#f8f9fa', border: '1px dashed #dee2e6', marginBottom: 10 }}>
              Ei vielä PTS-projekteja tälle kiinteistölle.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
              {ptsProjects.map(p => (
                <button key={p.id} type="button" onClick={() => pickPtsProject(p)}
                  style={{ textAlign: 'left', border: '1px solid #dee2e6', borderRadius: 6, padding: '8px 12px', background: '#fff', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
                  {p.name || p.nimi || `PTS ${p.id}`} <span style={{ fontWeight: 400, color: '#adb5bd' }}>{p.year}</span>
                </button>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={newPtsName} onChange={e => setNewPtsName(e.target.value)} placeholder="esim. Kuntoarvio 2026"
              style={{ flex: 1, border: '1px solid #ced4da', borderRadius: 3, padding: '6px 9px', fontSize: '0.82rem' }} />
            <button type="button" onClick={() => { createPtsProject(newPtsName); setNewPtsName(''); }}
              style={{ border: 'none', background: '#198754', color: '#fff', borderRadius: 4, padding: '7px 14px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>+ Luo PTS-projekti</button>
          </div>
        </div>
      )}

      {nav.ptsProjectId && (
        tarkastuskohteet.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
            <div style={{ flex: 1, textAlign: 'center', padding: '16px', color: '#6c757d', borderRadius: 6, background: '#f8f9fa', border: '1px dashed #dee2e6', fontSize: '0.82rem' }}>
              Ei vielä tarkastuskohteita. Lisää niitä Kuntoarviovalikossa.
            </div>
            {trailingTab && (
              <button type="button" disabled={navPending} style={trailingTabStyle(trailingTab.active, navPending)} onClick={trailingTab.onClick} {...hoverLift}>
                {trailingTab.label}
              </button>
            )}
          </div>
        ) : (
          <div>
            {isMobile ? (
              // Apple-style flat grouped list instead of a horizontally-
              // scrolling tab row or a dropdown — one accent color
              // (checkmark + text color, not a filled pill), hairline
              // dividers instead of per-item borders. A vertical list has
              // no overflow problem regardless of tarkastuskohde count
              // (unlike a horizontal row), so this also keeps PTS reachable
              // without needing a dropdown to collapse into.
              <div style={{ marginBottom: nav.tkId ? (showRakenneosaTabs ? 14 : 16) : 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 2px 7px' }}>
                  <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#8e8e93' }}>Tarkastuskohde</div>
                  {trailingTab && (
                    <button type="button" disabled={navPending} onClick={trailingTab.onClick}
                      style={{ padding: '5px 12px', borderRadius: 8, border: 'none', background: trailingTab.active ? '#198754' : '#f2f2f7', color: trailingTab.active ? '#fff' : '#198754', fontSize: '0.76rem', fontWeight: 700, cursor: navPending ? 'wait' : 'pointer' }}>
                      {trailingTab.label}
                    </button>
                  )}
                </div>
                <div style={{ background: '#fff', border: '1px solid #e5e5ea', borderRadius: 14, overflow: 'hidden' }}>
                  {tarkastuskohteet.map((tk, i) => {
                    const active = tk.id === nav.tkId && !trailingTab?.active;
                    return (
                      <button key={tk.id} type="button" disabled={navPending} onClick={() => openTk(tk)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                          padding: '12px 14px', border: 'none', background: 'none',
                          borderBottom: i < tarkastuskohteet.length - 1 ? '0.5px solid #e5e5ea' : 'none',
                          fontSize: '0.9rem', color: active ? '#198754' : '#1c1c1e', fontWeight: active ? 600 : 400,
                          cursor: navPending ? 'wait' : 'pointer', opacity: navPending ? 0.6 : 1,
                        }}>
                        <span style={{ flex: 1 }}>{tk.nimi}</span>
                        {active && <span style={{ color: '#198754' }}>✓</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* flexWrap:'wrap' used to let this row wrap on narrow screens
                 — harmless for plain tarkastuskohde tabs, but the divider +
                 trailingTab (PTS) pair at the end could then land orphaned
                 on their own line, detached from the row they're supposed
                 to be "connected" to. Scrolling horizontally instead of
                 wrapping keeps the whole row (including PTS) intact — only
                 relevant above 768px now, since below it the row is a
                 dropdown switcher instead (see isMobile branch) which can't
                 overflow regardless of tarkastuskohde count. */
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'nowrap', overflowX: 'auto', marginBottom: nav.tkId ? (showRakenneosaTabs ? 0 : 12) : 0 }}>
                {tarkastuskohteet.map(tk => (
                  <button key={tk.id} type="button" disabled={navPending} style={{ ...tabStyle(tk.id === nav.tkId && !trailingTab?.active, navPending), flexShrink: 0 }} onClick={() => openTk(tk)} {...hoverLift}>
                    {tk.nimi}
                  </button>
                ))}
                {trailingTab && (
                  <>
                    <div style={trailingDividerStyle} />
                    <button type="button" disabled={navPending} style={{ ...trailingTabStyle(trailingTab.active, navPending), flexShrink: 0 }} onClick={trailingTab.onClick} {...hoverLift}>
                      {trailingTab.label}
                    </button>
                  </>
                )}
              </div>
            )}

            {nav.tkId && showRakenneosaTabs && !trailingTab?.active && (
              isMobile ? (
                <div style={{ marginTop: 2 }}>
                  <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#8e8e93', margin: '0 2px 7px' }}>Rakenneosa</div>
                  <div style={{ background: '#fff', border: '1px solid #e5e5ea', borderRadius: 14, overflow: 'hidden' }}>
                    {rakenneosat.length === 0 && !canCreateRakenneosa ? (
                      <div style={{ padding: '14px', textAlign: 'center', color: '#8e8e93', fontSize: '0.82rem' }}>Ei vielä rakenneosia. Lisää niitä Poränäyte-lohkossa.</div>
                    ) : (
                      <>
                        {rakenneosat.map((rak, i) => {
                          const showTyyppi = rak.tyyppi && rak.tyyppi.trim().toLowerCase() !== (rak.nimi || '').trim().toLowerCase();
                          const active = rak.id === nav.rakId;
                          return (
                            <button key={rak.id} type="button" disabled={navPending} onClick={() => openRak(rak)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                                padding: '12px 14px', border: 'none', background: 'none',
                                borderBottom: (i < rakenneosat.length - 1 || canCreateRakenneosa) ? '0.5px solid #e5e5ea' : 'none',
                                fontSize: '0.9rem', color: active ? '#198754' : '#1c1c1e', fontWeight: active ? 600 : 400,
                                cursor: navPending ? 'wait' : 'pointer', opacity: navPending ? 0.6 : 1,
                              }}>
                              <span style={{ flex: 1 }}>{rak.nimi}{showTyyppi ? ` · ${rak.tyyppi}` : ''}</span>
                              {active && <span style={{ color: '#198754' }}>✓</span>}
                            </button>
                          );
                        })}
                        {/* Only Poränäyte can create rakenneosat — the other
                            blocks (Peitepaksuus, Vetolujuus, Ohuthie,
                            Kloridi) only ever pick among ones Poränäyte
                            already created. */}
                        {canCreateRakenneosa && (
                          <button type="button" onClick={openAddRak}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '12px 14px', border: 'none', background: 'none', color: '#198754', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}>
                            + Lisää rakenneosa
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  {extraActions && nav.rakId && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginTop: 10 }}>{extraActions}</div>
                  )}
                </div>
              ) : (
                <div>
                  {/* Tray "hangs" directly off the active tk tab (negative
                      margin + matching border, see tabStyle/trayStyle) so
                      everything inside it — empty state, pills, add button —
                      reads as that tab's own contents. */}
                  <div style={trayStyle}>
                    <div style={trayLabelStyle}>Rakenneosat</div>
                    {rakenneosat.length === 0 && !canCreateRakenneosa ? (
                      <div style={{ textAlign: 'center', padding: '12px', color: '#6c757d', borderRadius: 6, background: '#f8f9fa', border: '1px dashed #dee2e6', fontSize: '0.82rem' }}>
                        Ei vielä rakenneosia. Lisää niitä Poränäyte-lohkossa.
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        {rakenneosat.map(rak => {
                          const showTyyppi = rak.tyyppi && rak.tyyppi.trim().toLowerCase() !== (rak.nimi || '').trim().toLowerCase();
                          return (
                            <button key={rak.id} type="button" disabled={navPending} style={pillStyle(rak.id === nav.rakId, navPending)} onClick={() => openRak(rak)} {...hoverLift}>
                              {rak.nimi}{showTyyppi ? ` · ${rak.tyyppi}` : ''}
                            </button>
                          );
                        })}
                        {canCreateRakenneosa && (
                          <button type="button" onClick={openAddRak} {...hoverLift}
                            style={{ padding: '6px 14px', borderRadius: 8, border: '1px dashed #198754', background: '#fff', color: '#198754', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', transition: 'transform 0.15s ease, box-shadow 0.15s ease' }}>
                            + Rakenneosa
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Own row below the tray, only shown once one is actually
                      selected — before that it would just sit there disabled
                      and confusingly suggest this step is about samples. */}
                  {extraActions && nav.rakId && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginTop: 10 }}>{extraActions}</div>
                  )}
                </div>
              )
            )}
          </div>
        )
      )}

      {canCreateRakenneosa && showAddRak && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={closeAddRak}>
          <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(420px, 95vw)', padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>Lisää rakenneosa</div>

            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6c757d', marginBottom: 8 }}>
                Rakenneosa <span style={{ color: '#dc3545' }}>*</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                {rakTyypit.map(t => {
                  const sel = newRakNimi === t.value;
                  return (
                    <button key={t.value} type="button" onClick={() => { setNewRakNimi(t.value); setNewRakTyyppi(t.value); }}
                      style={{ padding: '5px 12px', borderRadius: 20, border: '1.5px solid', fontSize: '0.82rem', cursor: 'pointer',
                        fontWeight: sel ? 700 : 400,
                        borderColor: sel ? '#198754' : '#dee2e6',
                        background: sel ? '#f0fdf4' : '#fafafa',
                        color: sel ? '#15803d' : '#495057' }}>
                      {t.value}
                    </button>
                  );
                })}
              </div>
              <input type="text" placeholder="Tai kirjoita oma nimi… esim. Parveke 3, laatta" autoFocus
                value={newRakNimi}
                onChange={e => { setNewRakNimi(e.target.value); if (!rakTyypit.some(t => t.value === e.target.value)) setNewRakTyyppi(''); }}
                onKeyDown={e => { if (e.key === 'Enter' && trimmedNimi) submitAddRak(); if (e.key === 'Escape') closeAddRak(); }}
                style={{ width: '100%', border: '1px solid #ced4da', borderRadius: 4, padding: '7px 10px', fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box' }} />
              {dup && (
                <div style={{ fontSize: '0.75rem', color: '#95591a', marginTop: 6 }}>
                  Rakenneosa "{dup.nimi}" on jo olemassa tässä tarkastuskohteessa — samaa ei luoda kahdesti, vaan olemassa oleva avataan.
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <span style={{ fontSize: '0.72rem', color: '#6c757d', marginRight: 'auto', alignSelf: 'center' }}><span style={{ color: '#dc3545' }}>*</span> Pakollinen tieto</span>
              <button type="button" onClick={closeAddRak} className="btn btn-sm btn-outline-secondary">Peruuta</button>
              <button type="button" onClick={submitAddRak} disabled={!trimmedNimi}
                style={{ border: 'none', background: dup ? '#b0650f' : '#198754', color: '#fff', borderRadius: 4, padding: '7px 14px', fontSize: '0.8rem', fontWeight: 600, cursor: trimmedNimi ? 'pointer' : 'not-allowed', opacity: trimmedNimi ? 1 : 0.6 }}>
                {dup ? 'Avaa olemassa oleva' : 'Luo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
